import * as Sentry from '@sentry/vue'

// Third-party Promise.reject() / aborted fetches (Zendesk, GA, consent, …)
// land as "Non-Error promise rejection captured with value: undefined"
// with no stack — not actionable (see LUMA-FRONTEND-ZJ).
// Stale post-deploy chunks are recovered via reload (LUMA-FRONTEND-YQ).
const IGNORE_ERRORS = [
  /Non-Error promise rejection captured/,
  /Importing a module script failed/,
  /Failed to fetch dynamically imported module/,
  /Loading chunk [\da-z]+ failed/,
  /error loading dynamically imported module/i,
  /Loading CSS chunk [\da-z]+ failed/,
]

// Only keep exceptions whose stack frames come from our app (or local dev).
const ALLOW_URLS = [
  /^https?:\/\/([^.:/@]+\.)*luma-delikatessen\.ch(?::\d+)?(?:[/?#]|$)/,
  /^https?:\/\/localhost(?::\d+)?(?:[/?#]|$)/,
]

function isFirstPartyFrame(frame) {
  const filename = frame.filename
  if (!filename || filename === '<anonymous>' || filename === '[native code]') {
    return false
  }

  return ALLOW_URLS.some(pattern => pattern.test(filename))
}

async function lazyLoadSentryIntegrations() {
  // don't load on server
  if (window.sentIntegrationInit)
    return
  window.sentIntegrationInit = true
  Sentry.addIntegration(
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  )
}

function getSentryIntegrations() {
  // don't load on server
  if (window.sIntegrationLoaded)
    return []

  const browserTracing = Sentry.browserTracingIntegration()
  window.sIntegrationLoaded = true
  return [browserTracing]
}

export default defineNuxtPlugin({
  name: 'sentry',
  parallel: true,
  async setup(nuxtApp) {
    const vueApp = nuxtApp.vueApp

    const config = useRuntimeConfig()

    Sentry.init({
      app: vueApp,
      dsn: config.public?.sentry?.dsn ?? null,
      release: config.public?.sentry?.release ?? null,
      integrations: getSentryIntegrations(),

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: config.public.sentry?.tracesSampleRate,
      environment: config.public.sentry?.environment,

      // Events without a stacktrace are dropped in beforeSend below.
      ignoreErrors: IGNORE_ERRORS,
      allowUrls: ALLOW_URLS,

      beforeSend(event) {
        const frames = [
          ...(event.exception?.values ?? []).flatMap(
            value => value.stacktrace?.frames ?? [],
          ),
          ...(event.threads?.values ?? []).flatMap(
            thread => thread.stacktrace?.frames ?? [],
          ),
        ]

        // No stack → not debuggable; also covers message-only events and
        // unhandled rejections with undefined reason that slip past ignoreErrors.
        if (frames.length === 0) {
          return null
        }

        // Browser SDK marks all frames in_app:true — match filenames against
        // ALLOW_URLS instead (belt-and-suspenders with allowUrls).
        if (!frames.some(isFirstPartyFrame)) {
          return null
        }

        return event
      },

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

      // This sets the sample rate. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: config.public.sentry?.replaySampleRate,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: config.public.sentry?.errorReplaySampleRate,
    })

    // Lazy-load the replay integration to reduce bundle size
    lazyLoadSentryIntegrations()
  },
})
